/* The guide shows a full email at once. The short walkthrough is opt-in. */
(function () {
  'use strict';
  function mount() {
    const root = document.querySelector('[data-engine]');
    const source = document.getElementById('email-walkthrough-data');
    if (!root || !source || root.dataset.engineReady) return;
    let cases;
    try { cases = JSON.parse(source.textContent); } catch (_) { return; }
    if (!['cart', 'welcome', 'winback'].every(key => cases[key] && cases[key].document)) return;
    const field = name => root.querySelector(`[data-engine-${name}]`);
    const frame = field('email');
    const replay = field('replay');
    const download = field('download');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let selected = 'welcome';
    let stage = 3;
    let timer = null;
    let deadline = 0;
    let remaining = 0;
    let run = 0;
    let playing = false;
    let visible = !('IntersectionObserver' in window);
    let gone = false;
    let frameObserver;
    let objectURL;
    let fetching = false;
    let downloadRun = 0;
    let fetchController;

    function resizeEmail() {
      // Measure the content table, not scrollHeight of the iframe viewport.
      // That lets a shorter email shrink after a taller one, without a scrollbox.
      try {
        const table = frame.contentDocument.querySelector('body > table');
        if (!table) return;
        const height = Math.ceil(Math.max(table.getBoundingClientRect().height, table.scrollHeight)) + 2;
        if (height > 100 && height < 12000 && frame.style.height !== `${height}px`) frame.style.height = `${height}px`;
      } catch (_) { /* The initial document may not be ready yet. */ }
    }
    function watchEmail() {
      if (frameObserver) frameObserver.disconnect();
      try {
        const body = frame.contentDocument.body;
        if (!body) return;
        body.querySelectorAll('img').forEach(image => image.addEventListener('load', resizeEmail, { once: true }));
        if ('ResizeObserver' in window) {
          frameObserver = new ResizeObserver(resizeEmail);
          frameObserver.observe(body);
        }
        resizeEmail();
      } catch (_) { /* The preview stays readable while its document loads. */ }
    }
    frame.addEventListener('load', watchEmail);
    window.addEventListener('resize', resizeEmail);

    function clearTimer() {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    }
    function renderStage() {
      root.dataset.engineStage = String(stage);
      root.dataset.enginePlaying = String(playing && visible && !document.hidden && !gone);
      field('thinking').textContent = stage === 1 ? 'Read what they did' : stage === 2 ? 'Choose the right email' : 'Email ready';
      field('outcome').textContent = stage === 1 ? cases[selected].event + '.' : stage === 2 ? cases[selected].decision : cases[selected].outcome;
      replay.textContent = playing ? 'Start the steps again ↻' : 'Show the steps ↻';
    }
    function settle() {
      run += 1;
      clearTimer();
      playing = false;
      remaining = 0;
      stage = 3;
      renderStage();
    }
    function schedule() {
      if (!playing || !visible || document.hidden || gone || motion.matches || timer !== null) return;
      const token = run;
      deadline = performance.now() + remaining;
      timer = window.setTimeout(() => {
        timer = null;
        if (token !== run || !playing) return;
        stage += 1;
        if (stage >= 3) { settle(); return; }
        remaining = 1800;
        renderStage();
        schedule();
      }, remaining);
      renderStage();
    }
    function visibilityChanged() {
      if (!visible || document.hidden || gone) {
        if (timer !== null) remaining = Math.max(0, deadline - performance.now());
        clearTimer();
      } else schedule();
      renderStage();
    }
    function select(key) {
      if (!Object.prototype.hasOwnProperty.call(cases, key)) return;
      selected = key;
      const item = cases[key];
      settle();
      ['name', 'initials', 'tag', 'event', 'decision', 'detail', 'subject'].forEach(name => { field(name).textContent = item[name]; });
      field('recipient').textContent = item.name;
      item.activity.forEach((text, i) => { root.querySelector(`[data-engine-activity="${i}"]`).textContent = text; });
      item.facts.forEach((pair, i) => {
        root.querySelector(`[data-engine-fact-label="${i}"]`).textContent = pair[0];
        root.querySelector(`[data-engine-fact-value="${i}"]`).textContent = pair[1];
      });
      root.querySelectorAll('[data-engine-case]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.engineCase === key)));
      frame.title = `Email to ${item.name}: ${item.subject}`;
      frame.srcdoc = item.document;
      if (!fetching) field('download-status').textContent = '';
    }
    root.querySelectorAll('[data-engine-case]').forEach(button => {
      button.disabled = false;
      button.addEventListener('click', () => select(button.dataset.engineCase));
    });
    replay.disabled = false;
    replay.addEventListener('click', () => {
      settle();
      if (motion.matches || gone) return;
      stage = 1;
      playing = true;
      remaining = 1500;
      renderStage();
      schedule();
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.target === root && entry.isIntersecting);
        visibilityChanged();
      }, { threshold: 0 }).observe(root);
    }
    document.addEventListener('visibilitychange', visibilityChanged);
    motion.addEventListener?.('change', () => { if (motion.matches) settle(); });

    async function photoData(path, signal) {
      if (!/^\/images\/email-examples\/[a-z0-9-]+\.(jpg|png)$/.test(path)) throw new Error('Unsupported sample photo');
      const response = await fetch(path, { credentials: 'same-origin', signal });
      if (!response.ok) throw new Error('Photo not ready');
      const blob = await response.blob();
      if (blob.size > 600000 || !/^image\/(jpeg|png)$/.test(blob.type)) throw new Error('Unsupported sample photo');
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not read the photo'));
        reader.readAsDataURL(blob);
      });
    }
    download.disabled = false;
    download.addEventListener('click', async () => {
      if (fetching || gone) return;
      const item = cases[selected];
      const token = ++downloadRun;
      fetching = true;
      download.disabled = true;
      fetchController = new AbortController();
      field('download-status').textContent = 'Getting the email and photo…';
      try {
        let html = item.downloadDocument;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const paths = [...new Set([...doc.querySelectorAll('img')].map(image => image.getAttribute('src')))];
        for (const path of paths) {
          const data = await photoData(path, fetchController.signal);
          html = html.split(path).join(data);
        }
        if (token !== downloadRun || gone) return;
        if (objectURL) URL.revokeObjectURL(objectURL);
        objectURL = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = objectURL;
        link.download = item.filename;
        document.body.append(link);
        link.click();
        link.remove();
        field('download-status').textContent = `${item.tag === 'PAST BUYER' ? 'Past-buyer' : item.tag === 'WELCOME EMAIL' ? 'Welcome' : 'Cart'} email downloaded. Add your own details before you send.`;
      } catch (_) {
        if (token === downloadRun && !gone) field('download-status').textContent = 'The file could not download. Please try again.';
      } finally {
        if (token === downloadRun) { fetching = false; download.disabled = false; }
      }
    });
    window.addEventListener('pagehide', () => {
      gone = true;
      settle();
      downloadRun += 1;
      fetchController?.abort();
      fetching = false;
      download.disabled = false;
      if (objectURL) URL.revokeObjectURL(objectURL);
      objectURL = null;
      frameObserver?.disconnect();
    });
    window.addEventListener('pageshow', () => { gone = false; field('download-status').textContent = ''; watchEmail(); });
    root.dataset.engineReady = 'true';
    select(selected);
    watchEmail();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();
